import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

interface AdvancedAnalyticsContextType {
  isAdvancedMode: boolean;
  toggleAdvancedMode: () => void;
  setAdvancedMode: (enabled: boolean) => void;
}

const AdvancedAnalyticsContext = createContext<AdvancedAnalyticsContextType>({
  isAdvancedMode: false,
  toggleAdvancedMode: () => {},
  setAdvancedMode: () => {},
});

export const useAdvancedAnalytics = () => useContext(AdvancedAnalyticsContext);

interface AdvancedAnalyticsProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = '@DynamicDecisions:advancedAnalyticsMode';

// DYN-52. The header switch used to keep its OWN preference here, under a
// different name and with different values ('advanced'/'basic' rather than
// 'true'/'false'), and nothing connected the two. Anyone who ever flipped that
// switch has a value under this key and can see the switch sitting in the
// position it describes, so dropping it would move their switch back to Basic
// on next load, on top of never having given them what the label promised.
//
// Read only when this key has nothing to say, and retired afterwards. It is a
// migration rather than a second source of truth: leaving it readable would
// recreate the two-preferences problem that is the whole defect.
const RETIRED_TOGGLE_KEY = '@AnalyticsBot:dashboard_analytics_mode';

// PURE. It reads and returns, and removes nothing.
//
// The first version deleted the retired key here, which cost the migration the
// thing it existed to do: React calls a state initialiser TWICE under
// StrictMode, so the second call found the key already gone and returned
// false, discarding the preference. This context has been bitten by StrictMode
// once before, in the load-versus-persist race documented below, which is why
// the retirement now happens in an effect where running twice is harmless.
const readStoredPreference = (): boolean => {
  try {
    // THE RETIRED KEY WINS WHILE IT EXISTS, and the first version of this had
    // it the other way round, which a reviewer showed meant the migration
    // never reached the people it was written for.
    //
    // The two keys are not comparable records. The provider below persists on
    // mount, so anyone who has ever opened the dashboard has the current key
    // written as 'false' from the default state, whether or not they chose
    // anything. The retired key was written only by the old switch's own
    // handler, so it exists only if someone deliberately flipped it.
    //
    // Before this change nothing could write 'true' to the current key at all,
    // and that was checked across the whole history rather than reasoned about:
    // outside tests the only writer this key has ever had is the persist effect
    // below, which stores whatever the state already is, and the state could
    // only be true if this key already said 'true'. There was no way in.
    //
    // So a present retired key is the only deliberate signal there is, and it
    // takes precedence exactly once: the effect below removes it as soon as
    // the preference has been stored under the current key, and nothing writes
    // it again.
    const retired = localStorage.getItem(RETIRED_TOGGLE_KEY);
    if (retired !== null) return retired === 'advanced';
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Private mode or blocked storage: the toggle still works, it just does
    // not survive a reload.
    return false;
  }
};

export const AdvancedAnalyticsProvider: React.FC<AdvancedAnalyticsProviderProps> = ({ children }) => {
  // Read the saved preference in the initialiser rather than in an effect.
  //
  // It used to load in a mount effect while a second effect persisted on every
  // change. That second effect fires on mount too, with the default false, so
  // the pair raced: under StrictMode React runs mount effects twice, and the
  // load effect's second run read back the false the persist effect had just
  // written. The saved preference erased itself on every page load in dev.
  // Reading it here means there is no window in which the default is written
  // over a real value.
  const [isAdvancedMode, setIsAdvancedMode] = useState(readStoredPreference);

  // Persist to localStorage when changed, and only then retire the old key.
  //
  // The order is the point, and a reviewer found the version that got it
  // wrong. These were two effects, so a storage failure could leave the
  // current key unwritten while the removal succeeded anyway, destroying the
  // only record of the preference. The removal now happens after a write that
  // actually succeeded, and is skipped entirely when it did not.
  //
  // Retiring here rather than while READING is what keeps the read pure, which
  // StrictMode requires: it calls a state initialiser twice, and a read with a
  // side effect loses the value on the second call.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, isAdvancedMode.toString());
    } catch {
      // Nothing was stored, so the retired key is still the only record of
      // what this person chose. Leave it alone.
      return;
    }
    try {
      localStorage.removeItem(RETIRED_TOGGLE_KEY);
    } catch {
      /* not fatal, see above */
    }
  }, [isAdvancedMode]);

  const toggleAdvancedMode = () => {
    setIsAdvancedMode((prev) => !prev);
  };

  const setAdvancedMode = (enabled: boolean) => {
    setIsAdvancedMode(enabled);
  };

  return (
    <AdvancedAnalyticsContext.Provider
      value={{
        isAdvancedMode,
        toggleAdvancedMode,
        setAdvancedMode,
      }}
    >
      {children}
    </AdvancedAnalyticsContext.Provider>
  );
};

export default AdvancedAnalyticsContext;